import React from "react";
import { Controller, Control, useWatch, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";

export type Sex = "" | "M" | "F" | "X";
export type EthnicityCode = "" | "1" | "2" | "3";
export type DisabledCode = "" | "1" | "2" | "3";
export type RaceCode =
  | "1"
  | "2"
  | "3"
  | "4a"
  | "4b"
  | "4c"
  | "4d"
  | "4e"
  | "4f"
  | "4g"
  | "5a"
  | "5b"
  | "5c"
  | "5d"
  | "6"
  | "7";

export interface DemographicsValue {
  declined: boolean;
  sex: Sex;
  ethnicity: EthnicityCode;
  race: RaceCode[];
  disabled: DisabledCode;
}

export const emptyDemographics: DemographicsValue = {
  declined: false,
  sex: "",
  ethnicity: "",
  race: [],
  disabled: "",
};

const RACE_VALUES: RaceCode[] = [
  "1",
  "2",
  "3",
  "4a",
  "4b",
  "4c",
  "4d",
  "4e",
  "4f",
  "4g",
  "5a",
  "5b",
  "5c",
  "5d",
  "6",
  "7",
];

const RACE_LABEL_KEY: Record<RaceCode, string> = {
  "1": "raceWhite",
  "2": "raceBlack",
  "3": "raceAmericanIndian",
  "4a": "raceAsianIndian",
  "4b": "raceChinese",
  "4c": "raceFilipino",
  "4d": "raceJapanese",
  "4e": "raceKorean",
  "4f": "raceVietnamese",
  "4g": "raceOtherAsian",
  "5a": "raceNativeHawaiian",
  "5b": "raceGuamanian",
  "5c": "raceSamoan",
  "5d": "raceOtherPacific",
  "6": "raceOther",
  "7": "didNotRespond",
};

interface Props {
  control: Control<any>;
  basePath: string; // "" for root-level, e.g. "members.0" for nested
  subjectLabel?: string; // e.g. "for Jane Doe"
  thirdPerson?: boolean; // true when filled on behalf of another person (e.g. minor)
}

export const DemographicsFields: React.FC<Props> = ({
  control,
  basePath,
  subjectLabel,
  thirdPerson = false,
}) => {
  const { t } = useTranslation();
  const path = (field: string) => (basePath ? `${basePath}.${field}` : field);

  const declined = useWatch({
    control,
    name: path("declined"),
  });
  const race = useWatch({ control, name: path("race") }) as
    | RaceCode[]
    | undefined;
  const raceCount = Array.isArray(race) ? race.length : 0;

  const { errors } = useFormState({
    control,
    name: [path("sex"), path("ethnicity"), path("race"), path("disabled")],
  });
  const errorAt = (field: string): string | undefined => {
    const segments = path(field).split(".");
    let node: any = errors;
    for (const seg of segments) {
      if (!node) return undefined;
      node = node[seg];
    }
    return node?.message as string | undefined;
  };
  const sexError = errorAt("sex");
  const ethnicityError = errorAt("ethnicity");
  const raceError = errorAt("race");
  const disabledError = errorAt("disabled");

  const tx = (key: string) =>
    t(`affordable.application.demographics.${key}`) as string;

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography
        color="primary"
        sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}
      >
        {tx("title")}
        {subjectLabel ? ` ${subjectLabel}` : ""}
      </Typography>
      <Typography
        sx={{ fontSize: 12, fontWeight: 400, color: "text.secondary", mb: 1.5 }}
      >
        {tx("helper")}
      </Typography>

      <Controller
        name={path("declined")}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Checkbox
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label={tx(thirdPerson ? "optOutThirdPerson" : "optOut")}
          />
        )}
      />

      {!declined && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name={path("sex")}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!sexError}>
                  <InputLabel>{`${tx("sexLabel")} *`}</InputLabel>
                  <Select {...field} label={`${tx("sexLabel")} *`}>
                    <MenuItem value="M">{tx("sexMale")}</MenuItem>
                    <MenuItem value="F">{tx("sexFemale")}</MenuItem>
                    <MenuItem value="X">{tx("didNotRespond")}</MenuItem>
                  </Select>
                  {sexError && <FormHelperText>{sexError}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name={path("ethnicity")}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!ethnicityError}>
                  <InputLabel>{`${tx("ethnicityLabel")} *`}</InputLabel>
                  <Select {...field} label={`${tx("ethnicityLabel")} *`}>
                    <MenuItem value="1">{tx("ethnicityHispanic")}</MenuItem>
                    <MenuItem value="2">{tx("ethnicityNotHispanic")}</MenuItem>
                    <MenuItem value="3">{tx("didNotRespond")}</MenuItem>
                  </Select>
                  {ethnicityError && (
                    <FormHelperText>{ethnicityError}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name={path("race")}
              control={control}
              render={({ field }) => {
                const value = (field.value as RaceCode[]) || [];
                return (
                  <FormControl fullWidth error={!!raceError}>
                    <InputLabel>{`${tx("raceLabel")} *`}</InputLabel>
                    <Select
                      multiple
                      value={value}
                      onChange={(e) => {
                        const next = e.target.value as RaceCode[];
                        if (next.length > 5) return; // hard cap at 5
                        field.onChange(next);
                      }}
                      label={`${tx("raceLabel")} *`}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(selected as RaceCode[]).map((v) => (
                            <Chip
                              key={v}
                              label={tx(RACE_LABEL_KEY[v])}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {RACE_VALUES.map((code) => {
                        const checked = value.includes(code);
                        const disableUnchecked = !checked && raceCount >= 5;
                        return (
                          <MenuItem
                            key={code}
                            value={code}
                            disabled={disableUnchecked}
                          >
                            <Checkbox checked={checked} size="small" />
                            <ListItemText primary={tx(RACE_LABEL_KEY[code])} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: raceError ? "error.main" : "text.secondary",
                        mx: 0,
                      }}
                    >
                      {raceError || tx("raceHelper")}
                    </FormHelperText>
                  </FormControl>
                );
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              color="primary"
              sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
            >
              {`${tx("disabledLabel")} *`}
            </Typography>
            <Controller
              name={path("disabled")}
              control={control}
              render={({ field }) => (
                <FormControl error={!!disabledError}>
                  <RadioGroup
                    row
                    {...field}
                    sx={{ gap: 3, "& .MuiFormControlLabel-root": { mr: 0 } }}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio size="small" />}
                      label={tx("yes")}
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio size="small" />}
                      label={tx("no")}
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio size="small" />}
                      label={tx("didNotRespond")}
                    />
                  </RadioGroup>
                  {disabledError && (
                    <FormHelperText>{disabledError}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
